import { ApiClient } from '@gopinhos/shared/api';

/**
 * Resolve the API base URL.
 *
 * Priority:
 *   1. VITE_API_URL (set at build time via apps/web/.env.production or CI env)
 *   2. Production domain auto-detect — when the SPA is served from
 *      app.bendersia.com or *.gopinhos.pages.dev, hit api.bendersia.com so
 *      a forgotten env var never silently breaks login.
 *   3. Localhost fallback for dev.
 */
function resolveApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  if (fromEnv && fromEnv.length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.bendersia.com' || host.endsWith('.gopinhos.pages.dev')) {
      return 'https://api.bendersia.com';
    }
  }
  return 'http://localhost:3000';
}

const baseUrl = resolveApiBaseUrl();

interface AccessTokenStore {
  get: () => string | null;
  set: (token: string | null, expiresInSeconds?: number) => void;
}

let accessTokenStore: AccessTokenStore = {
  get: () => null,
  set: () => undefined,
};

export function bindAccessTokenStore(store: AccessTokenStore): void {
  accessTokenStore = store;
}

let refreshInflight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const res = await fetch(new URL('/api/v1/auth/refresh', baseUrl).toString(), {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const json = (await res.json()) as { accessToken: string; expiresIn: number };
      accessTokenStore.set(json.accessToken, json.expiresIn);
      return true;
    } catch {
      return false;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export const apiClient = new ApiClient({
  baseUrl,
  getAccessToken: () => accessTokenStore.get(),
  onUnauthorized: refreshAccessToken,
});

export type AccessTokenSubscriber = (token: string | null) => void;
